import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Editor from '@monaco-editor/react';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import FormatAlignLeftRoundedIcon from '@mui/icons-material/FormatAlignLeftRounded';
import WrapTextRoundedIcon from '@mui/icons-material/WrapTextRounded';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import { toMonacoLanguage, getLanguageLabel } from './languageManager';
import { registerEditorThemes, resolveThemeId, EDITOR_THEMES, DEFAULT_THEME } from './themeManager';
import { registerSnippetCompletions } from './snippetManager';
import { registerBasicDiagnostics } from './suggestionEngine';
import { bindEditorShortcuts } from './shortcutHandler';
import { bindHtmlEmmetExpand, bindHtmlAutoCloseTags } from './emmetExpand';
import { formatDocument } from './formatter';
import EditorStatusBar from './EditorStatusBar';
import EditorBreadcrumb, { editorFileNameForLanguage } from './EditorBreadcrumb';

const EDITOR_OPTIONS = {
  fontSize: 14,
  lineHeight: 21,
  fontFamily: "'Cascadia Code', 'Cascadia Mono', Consolas, 'Courier New', monospace",
  fontLigatures: true,
  minimap: { enabled: true, scale: 1, showSlider: 'mouseover' },
  scrollBeyondLastLine: false,
  smoothScrolling: true,
  cursorBlinking: 'smooth',
  cursorSmoothCaretAnimation: 'on',
  renderLineHighlight: 'all',
  bracketPairColorization: { enabled: true },
  autoClosingBrackets: 'languageDefined',
  autoClosingQuotes: 'languageDefined',
  // VS Code defaults for HTML learners
  autoClosingTags: true,
  linkedEditing: true,
  autoIndent: 'full',
  formatOnPaste: true,
  formatOnType: true,
  quickSuggestions: true,
  suggestOnTriggerCharacters: true,
  acceptSuggestionOnEnter: 'on',
  tabCompletion: 'on',
  wordBasedSuggestions: 'matchingDocuments',
  parameterHints: { enabled: true },
  hover: { enabled: true },
  contextmenu: true,
  folding: true,
  foldingStrategy: 'auto',
  showFoldingControls: 'mouseover',
  matchBrackets: 'always',
  renderWhitespace: 'selection',
  scrollbar: {
    vertical: 'auto',
    horizontal: 'auto',
    verticalScrollbarSize: 10,
    horizontalScrollbarSize: 10,
    useShadows: false,
  },
  tabSize: 4,
  insertSpaces: true,
  multiCursorModifier: 'alt',
  mouseWheelZoom: true,
  padding: { top: 8 },
};

function runCleanup(item) {
  if (typeof item === 'function') {
    item();
    return;
  }
  if (item && typeof item.dispose === 'function') {
    item.dispose();
  }
}

function clearCleanups(cleanups) {
  cleanups.forEach(runCleanup);
  cleanups.length = 0;
}

export default function MonacoEditorWidget({
  value,
  onChange,
  language = 'python',
  readOnly = false,
  height = 480,
  themeId = DEFAULT_THEME,
  onThemeChange,
  showToolbar = true,
  showStatusBar = true,
  onEditorMount,
  contentId,
  followAppTheme = false,
  fileName,
  showBreadcrumb = true,
  fillHeight = false,
  onSave,
  onSaveAs,
}) {
  const muiTheme = useTheme();
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const languageRef = useRef(language);
  const saveRef = useRef(onSave);
  const saveAsRef = useRef(onSaveAs);
  const cleanupRef = useRef([]);
  const [cursor, setCursor] = useState({ line: 1, column: 1 });

  languageRef.current = language;
  saveRef.current = onSave;
  saveAsRef.current = onSaveAs;
  const [selectedChars, setSelectedChars] = useState(0);
  const [wordWrap, setWordWrap] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTheme, setActiveTheme] = useState(themeId || DEFAULT_THEME);

  const effectiveTheme = useMemo(() => {
    if (followAppTheme) {
      return muiTheme.palette.mode === 'light' ? 'vs-light' : 'vs-dark';
    }
    return activeTheme;
  }, [followAppTheme, muiTheme.palette.mode, activeTheme]);

  const monacoLanguage = useMemo(() => toMonacoLanguage(language), [language]);
  const displayFileName = fileName || editorFileNameForLanguage(language);
  const isMinimalChrome = !showToolbar && !showStatusBar && !showBreadcrumb;

  const handleEditorMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;

      clearCleanups(cleanupRef.current);

      registerEditorThemes(monaco);
      monaco.editor.setTheme(resolveThemeId(effectiveTheme));

      cleanupRef.current.push(registerSnippetCompletions(monaco, language));
      cleanupRef.current.push(registerBasicDiagnostics(monaco, editor, language));
      cleanupRef.current.push(
        bindEditorShortcuts(editor, monaco, {
          onSave: () => saveRef.current?.(),
          onSaveAs: () => saveAsRef.current?.(),
        }),
      );
      cleanupRef.current.push(
        bindHtmlEmmetExpand(editor, monaco, () => languageRef.current),
      );
      cleanupRef.current.push(
        bindHtmlAutoCloseTags(editor, monaco, () => languageRef.current),
      );

      cleanupRef.current.push(
        editor.onDidChangeCursorPosition(({ position }) => {
          setCursor({ line: position.lineNumber, column: position.column });
        }),
      );

      cleanupRef.current.push(
        editor.onDidChangeCursorSelection(({ selection }) => {
          const model = editor.getModel();
          if (!model) return;
          setSelectedChars(model.getValueInRange(selection).length);
        }),
      );

      onEditorMount?.(editor, monaco);
    },
    [effectiveTheme, language, onEditorMount],
  );

  useEffect(() => {
    return () => {
      clearCleanups(cleanupRef.current);
    };
  }, []);

  useEffect(() => {
    if (monacoRef.current) {
      monacoRef.current.editor.setTheme(resolveThemeId(effectiveTheme));
    }
  }, [effectiveTheme]);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.updateOptions({ readOnly, wordWrap: wordWrap ? 'on' : 'off' });
    }
  }, [readOnly, wordWrap]);

  // Re-register language-specific completions when language changes.
  useEffect(() => {
    if (!monacoRef.current || !editorRef.current) return undefined;
    const monaco = monacoRef.current;
    const snippetDisposable = registerSnippetCompletions(monaco, language);
    return () => {
      if (typeof snippetDisposable === 'function') snippetDisposable();
      else snippetDisposable?.dispose?.();
    };
  }, [language]);

  const handleThemeChange = (nextTheme) => {
    setActiveTheme(nextTheme);
    onThemeChange?.(nextTheme);
  };

  const toggleFullscreen = () => setIsFullscreen((prev) => !prev);

  const isLight = effectiveTheme === 'vs-light' || effectiveTheme === 'vs';
  const containerSx = {
    border: fillHeight ? 'none' : '1px solid #3C3C3C',
    borderRadius: 0,
    overflow: 'hidden',
    bgcolor: isLight ? '#FFFFFF' : '#1E1E1E',
    display: fillHeight ? 'flex' : 'block',
    flexDirection: fillHeight ? 'column' : undefined,
    flex: fillHeight ? 1 : undefined,
    minHeight: fillHeight ? 0 : undefined,
    height: fillHeight ? '100%' : undefined,
    ...(isFullscreen
      ? {
          position: 'fixed',
          inset: 0,
          zIndex: 1400,
          height: '100vh',
        }
      : {}),
  };

  const editorHeight = isFullscreen
    ? 'calc(100vh - 72px)'
    : fillHeight
      ? '100%'
      : height;

  return (
    <Box sx={containerSx}>
      {showBreadcrumb ? <EditorBreadcrumb fileName={displayFileName} /> : null}
      {showToolbar ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1,
            py: 0.5,
            bgcolor: '#252526',
            borderBottom: '1px solid #3C3C3C',
            minHeight: 36,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: '#CCCCCC', fontWeight: 600, letterSpacing: 0.4 }}
          >
            {getLanguageLabel(language)}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {!followAppTheme ? (
            <Select
              size="small"
              value={activeTheme}
              onChange={(e) => handleThemeChange(e.target.value)}
              sx={{
                color: '#CCCCCC',
                fontSize: 12,
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#3C3C3C' },
                '.MuiSvgIcon-root': { color: '#CCCCCC' },
                height: 28,
                minWidth: { xs: 110, sm: 140 },
                maxWidth: { xs: 140, sm: 'none' },
              }}
              MenuProps={{
                PaperProps: {
                  sx: { bgcolor: '#252526', color: '#CCCCCC', maxHeight: 320 },
                },
              }}
            >
              {Object.values(EDITOR_THEMES).map((theme) => (
                <MenuItem key={theme.id} value={theme.id} sx={{ fontSize: 13 }}>
                  {theme.label}
                </MenuItem>
              ))}
            </Select>
            ) : null}
            <Tooltip title="Format document (Shift+Alt+F)">
              <span>
                <IconButton
                  size="small"
                  disabled={readOnly}
                  onClick={() => formatDocument(editorRef.current)}
                  sx={{ color: '#CCCCCC' }}
                >
                  <FormatAlignLeftRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Toggle word wrap">
              <IconButton
                size="small"
                onClick={() => setWordWrap((prev) => !prev)}
                sx={{ color: wordWrap ? '#007ACC' : '#CCCCCC' }}
              >
                <WrapTextRoundedIcon fontSize="small" />
            </IconButton>
            </Tooltip>
            <Tooltip title="Zoom in (Ctrl++)">
              <IconButton
                size="small"
                onClick={() => editorRef.current?.trigger('toolbar', 'editor.action.fontZoomIn', null)}
                sx={{ color: '#CCCCCC' }}
              >
                <ZoomInRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom out (Ctrl+-)">
              <IconButton
                size="small"
                onClick={() => editorRef.current?.trigger('toolbar', 'editor.action.fontZoomOut', null)}
                sx={{ color: '#CCCCCC' }}
              >
                <ZoomOutRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <IconButton size="small" onClick={toggleFullscreen} sx={{ color: '#CCCCCC' }}>
                {isFullscreen ? (
                  <FullscreenExitRoundedIcon fontSize="small" />
                ) : (
                  <FullscreenRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      ) : null}

      <Box sx={fillHeight ? { flex: 1, minHeight: 0 } : undefined}>
      <Editor
        height={editorHeight}
        language={monacoLanguage}
        theme={resolveThemeId(effectiveTheme)}
        value={value || ''}
        onChange={(nextValue) => onChange?.(nextValue ?? '')}
        onMount={handleEditorMount}
        loading={
          <Box sx={{ p: 2, color: '#CCCCCC', bgcolor: '#1E1E1E', fontFamily: 'monospace' }}>
            Loading editor...
          </Box>
        }
        options={{
          ...EDITOR_OPTIONS,
          readOnly,
          wordWrap: wordWrap ? 'on' : 'off',
          ...(isMinimalChrome
            ? { minimap: { enabled: false }, padding: { top: 4 } }
            : {}),
        }}
      />
      </Box>

      {showStatusBar ? (
        <EditorStatusBar
          language={getLanguageLabel(language)}
          line={cursor.line}
          column={cursor.column}
          selectedChars={selectedChars}
          tabSize={4}
          themeLabel={EDITOR_THEMES[effectiveTheme]?.label || 'VS Code Dark+'}
          wordWrap={wordWrap}
        />
      ) : null}
    </Box>
  );
}
